/**
 * lib/vault.ts — broker vault client. Bridges the terminal-ts SPA to the
 * server's E2E-encrypted broker vault and the /api/broker/passthrough
 * endpoint.
 *
 * THE CONTRACT:
 *   The server only ever holds ciphertext + IV + salt + KDF metadata.
 *   The plaintext broker API key is decrypted in vault.html (separate
 *   page), held in sessionStorage under key 'terminal-vault-unlocked',
 *   and read here for each broker call.
 *
 *   If sessionStorage is empty when the SPA loads, we redirect the user
 *   to vault.html with a return URL so they can unlock and come back.
 */

import { useStore } from '@/store';
import { API_BASE } from '@/lib/api';
import { describeApiError, type RouterErrorBody } from '@/lib/apiErrors';
import { passthroughConfirmedMode } from '@/lib/tradingMode';

const SESSION_VAULT_KEY = 'terminal-vault-unlocked';

export interface UnlockedVault {
  broker: string;        // alpaca | alpaca_paper | ibkr | tradovate_live | tradovate_demo
  key:    string;        // plaintext broker API key (memory only)
  secret: string | null; // optional secret (Alpaca only)
}

/** Session state written by vault.html. One entry per unlocked broker. */
interface VaultState {
  v: 2;
  active: string;
  brokers: Record<string, { key: string; secret: string | null }>;
}

/**
 * Every broker unlocked in this session.
 *
 * The store used to hold exactly ONE entry, so unlocking IBKR silently
 * discarded Alpaca. Multi-broker execution needs a credential per account it
 * fires into; with one slot available, the terminal's multi-fire sent the
 * legs it had no key for into the SHARED platform account and reported
 * success. Holding several is what makes "one click, MY accounts" true.
 *
 * Accepts the pre-2026-08-03 single-entry shape as well, because a tab opened
 * before the change is still holding one.
 */
export function getUnlockedBrokers(): Record<string, { key: string; secret: string | null }> {
  try {
    const raw = sessionStorage.getItem(SESSION_VAULT_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as VaultState | UnlockedVault;
    if ((v as VaultState)?.v === 2 && (v as VaultState).brokers) {
      return (v as VaultState).brokers;
    }
    const one = v as UnlockedVault;
    if (one?.broker && one?.key) return { [one.broker]: { key: one.key, secret: one.secret ?? null } };
    return {};
  } catch {
    return {};
  }
}

/** The broker most recently unlocked. Used by the single-account paths. */
export function getUnlockedVault(): UnlockedVault | null {
  try {
    const raw = sessionStorage.getItem(SESSION_VAULT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as VaultState | UnlockedVault;
    if ((v as VaultState)?.v === 2 && (v as VaultState).brokers) {
      const st = v as VaultState;
      const name = st.active && st.brokers[st.active] ? st.active : Object.keys(st.brokers)[0];
      const e = name ? st.brokers[name] : null;
      return e ? { broker: name, key: e.key, secret: e.secret ?? null } : null;
    }
    const one = v as UnlockedVault;
    if (!one?.broker || !one?.key) return null;
    return one;
  } catch {
    return null;
  }
}

export function isVaultUnlocked(): boolean {
  return getUnlockedVault() !== null;
}

// ── Event venues (prediction markets: Kalshi, Polymarket US) ──────────────
export type EventVenueName = 'kalshi' | 'polymarket_us';
export interface UnlockedEventCreds {
  venue:      EventVenueName;
  apiKeyId:   string;
  privateKey: string;          // RSA PEM (Kalshi) / Ed25519 (Polymarket US)
  mode:       'demo' | 'prod';
}

/**
 * Unlocked credentials for an event venue, read from the SAME vault the brokers
 * use. Entries are named "kalshi" / "kalshi_demo" / "polymarket_us" /
 * "polymarket_us_demo"; the "_demo" suffix selects the sandbox account, mirroring
 * alpaca vs alpaca_paper. Returns null when that venue is not unlocked -- the
 * caller then REFUSES the order rather than routing anywhere else.
 */
export function getUnlockedEventVenue(venue: EventVenueName): UnlockedEventCreds | null {
  const entries = getUnlockedBrokers();
  for (const [name, e] of Object.entries(entries)) {
    if (!e?.key) continue;
    if (name.replace(/_demo$/, '') === venue) {
      return { venue, apiKeyId: e.key, privateKey: e.secret ?? '', mode: name.endsWith('_demo') ? 'demo' : 'prod' };
    }
  }
  return null;
}

export function isEventVenueUnlocked(venue: EventVenueName): boolean {
  return getUnlockedEventVenue(venue) !== null;
}

/**
 * Clear the unlocked vault from this session.
 *
 * The overwrite-then-remove below is theatre and is documented as such:
 * JavaScript strings are immutable, so writing zeros creates a NEW string
 * and leaves the original plaintext sitting in the heap until GC. It costs
 * nothing to keep (it does clear the storage slot if removal is somehow
 * blocked), but nobody should read it as secure erasure.
 *
 * The real mitigation is that the key never leaves sessionStorage for a
 * long-lived store and dies with the tab. If that is not strong enough for
 * a given customer, the fix is a non-extractable CryptoKey in IndexedDB
 * (as the Taskforces signing key now uses), not a longer wipe routine.
 */
export function lockVault(): void {
  try {
    sessionStorage.setItem(SESSION_VAULT_KEY, '0'.repeat(64));
  } catch { /* storage may be full or blocked; removal below is what matters */ }
  sessionStorage.removeItem(SESSION_VAULT_KEY);
}

export function redirectToVault(): void {
  // MUST be SAME-ORIGIN. The unlock writes the decrypted key to sessionStorage,
  // and sessionStorage is scoped per origin: sending the user to
  // omnistrat.ai/terminal/vault.html wrote the unlock under THAT origin, so this
  // SPA (terminal.omnistrat.ai) could never see it and showed "NOT CONNECTED"
  // no matter how many times the user unlocked. vault.html is now served from
  // this app's own public/ directory, so the unlock lands where we read it.
  const here = encodeURIComponent(window.location.href);
  window.location.href = `/vault.html?return=${here}`;
}

/**
 * Calls a broker API through the server's passthrough. The server reads
 * the broker key from the request body, forwards once to the broker,
 * and never persists the key. Server-side audit log records only
 * { user, broker, endpoint, ts } — never the key.
 */
export async function brokerCall<T = unknown>(
  endpoint: string,
  init: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: unknown;
    /**
     * True ONLY after the trader explicitly confirmed live trading for this
     * call. The router treats every non-GET passthrough as order-bearing and
     * refuses one to a real-money host (alpaca, ibkr, tradovate_live) without
     * confirmed_mode "live" (409 TRADING_MODE_MISMATCH). We refuse it here
     * first rather than send something we know will be refused.
     */
    liveConfirmed?: boolean;
  } = {},
): Promise<T> {
  const v = getUnlockedVault();
  if (!v) {
    redirectToVault();
    throw new Error('vault_locked');
  }
  const method = init.method ?? 'GET';
  const cm = passthroughConfirmedMode(v.broker, method, init.liveConfirmed === true);
  if (cm === 'REFUSE') {
    throw new Error(`Confirm live trading: ${method} ${endpoint} goes to a REAL-MONEY ${v.broker} account and was not confirmed as live. Nothing was sent.`);
  }
  const token = useStore.getState().token;
  const r = await fetch(`${API_BASE}/api/broker/passthrough`, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      broker:            v.broker,
      broker_api_key:    v.key,
      broker_api_secret: v.secret,
      endpoint,
      method,
      body:              init.body,
      ...(cm === 'live' ? { confirmed_mode: 'live' } : {}),
    }),
  });
  if (!r.ok) {
    let body: unknown = null;
    try { body = await r.json(); } catch { /* not JSON */ }
    throw new Error(`Broker passthrough: ${describeApiError(r.status, body as RouterErrorBody | null).message} (HTTP ${r.status})`);
  }
  return r.json() as Promise<T>;
}

/** Full session teardown: clear vault, revoke Passport token, redirect to login. */
export async function fullLogout(): Promise<void> {
  const token = useStore.getState().token;
  // Clear the LOCAL session first so logout is instant and can never be blocked
  // by a slow or unreachable /auth/logout. The previous version AWAITED that
  // fetch before redirecting, so a hung or cross-origin-stalled request left the
  // button looking completely dead -- the user clicked and nothing happened.
  lockVault();
  useStore.getState().logout();
  try { localStorage.removeItem('omnistrat-token'); } catch { /* private mode */ }
  try { sessionStorage.removeItem('omnistrat_token'); } catch { /* private mode */ }
  // Best-effort server-side revoke: fire-and-forget with keepalive so it still
  // completes as the page navigates away. Never awaited -> never blocks the
  // redirect.
  if (token) {
    try {
      void fetch(`${API_BASE}/auth/logout`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    } catch { /* ignore */ }
  }
  window.location.href = 'https://omnistrat.ai/login.html';
}
